/**
 * React Query hooks for Audit Log API (COMP-006).
 * IP-006: admin/auditor audit log query, export, retention, verify.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type {
  AuditLogEntry,
  RetentionPolicy,
  IntegrityVerificationResult,
  PaginatedResponse,
} from "@/types";

interface AuditLogParams {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  eventType?: string;
  severity?: string;
  actor?: string;
  target?: string;
  sortColumn?: string;
  sortDirection?: string;
  liveUpdates?: boolean;
}

export function useAuditLog(params: AuditLogParams = {}) {
  const api = useApiClient();
  const { page = 1, pageSize = 50, from, to, eventType, severity, actor, target, sortColumn, sortDirection, liveUpdates = true } = params;

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("pageSize", String(pageSize));
  if (from) queryParams.set("startDate", `${from}T00:00:00.000Z`);
  if (to) queryParams.set("endDate", `${to}T23:59:59.999Z`);
  if (eventType) queryParams.set("eventTypes", eventType);
  if (severity) queryParams.set("severity", severity);
  if (sortColumn) queryParams.set("sortColumn", sortColumn);
  if (sortDirection) queryParams.set("sortDirection", sortDirection);
  // Backend "search" does CONTAINS across actorDisplayName, targetResourceName, description
  const searchTerm = actor || target;
  if (searchTerm) queryParams.set("search", searchTerm);

  return useQuery({
    queryKey: ["audit", { page, pageSize, from, to, eventType, severity, actor, target, sortColumn, sortDirection }],
    queryFn: () =>
      api.get<PaginatedResponse<AuditLogEntry>>(
        `/api/admin/audit?${queryParams.toString()}`,
      ),
    staleTime: 10_000,
    refetchInterval: liveUpdates ? 15_000 : false, // Live view: auto-refresh every 15 seconds
  });
}

export function useAuditExport(params: {
  format: "csv" | "json";
  from?: string;
  to?: string;
  eventType?: string;
}) {
  const api = useApiClient();

  const queryParams = new URLSearchParams();
  queryParams.set("format", params.format);
  if (params.from) queryParams.set("startDate", `${params.from}T00:00:00.000Z`);
  if (params.to) queryParams.set("endDate", `${params.to}T23:59:59.999Z`);
  if (params.eventType) queryParams.set("eventTypes", params.eventType);

  return useQuery({
    queryKey: ["audit", "export", params],
    queryFn: () =>
      api.get<Blob>(`/api/admin/audit/export?${queryParams.toString()}`),
    enabled: false, // Only fetch on demand
  });
}

export function useRetentionPolicy() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["audit", "retention"],
    queryFn: () => api.get<RetentionPolicy>("/api/admin/audit/retention"),
    staleTime: 300_000,
  });
}

export function useUpdateRetention() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policy: RetentionPolicy) =>
      api.put<RetentionPolicy>("/api/admin/audit/retention", policy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["audit", "retention"] });
    },
  });
}

export function useVerifyIntegrity() {
  const api = useApiClient();

  return useMutation({
    mutationFn: (params: { tenantId: string; from?: string; to?: string }) =>
      api.post<IntegrityVerificationResult>(
        "/api/admin/audit/verify",
        params,
      ),
  });
}
